import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useApi } from './useApi.js'
import { useRealtime } from './useSubscription.js'

// useApi + realtime invalidation — the Phase-5 workhorse. Each page element
// declares its data and its topics and updates independently:
//
//   const { data, loading, error, refetch } = useLiveQuery(
//     '/api/v1/messages/conversations/42', { page: 1 },
//     { topics: [me && `user.${me.clerk_user_id}.messages`] },
//   )
//
// options:
//   topics  — array of topic strings; falsy entries are skipped (ids still
//             loading). When any topic fires, the query is invalidated
//             (refetches while mounted); the same happens after a reconnect,
//             closing any missed-event gap.
//   onEvent — optional (evt) => {...} called with { type, topic, data } before
//             the refetch; return false to suppress the automatic refetch
//             (e.g. after patching the cache manually).
//   enabled — as in useApi (also gates the subscriptions).
//
// Returns the useApi shape: { data, loading, error, refetch }.
export function useLiveQuery(path, params, options = {}) {
  const { topics = [], onEvent, enabled } = options
  const result = useApi(path, params, { enabled, onSuccess: options.onSuccess, query: options.query })
  const { manager } = useRealtime()
  const queryClient = useQueryClient()

  const onEventRef = useRef(onEvent)
  useEffect(() => {
    onEventRef.current = onEvent
  })

  // The query key at event time — kept in a ref so subscriptions survive
  // params identity churn without resubscribing.
  const keyRef = useRef(null)
  keyRef.current = [path, params ?? null]

  const activeTopics = topics.filter(Boolean)
  const topicsKey = JSON.stringify(activeTopics)
  const liveEnabled = enabled !== false && activeTopics.length > 0

  useEffect(() => {
    if (!liveEnabled) return undefined
    const list = JSON.parse(topicsKey)
    const invalidate = () => queryClient.invalidateQueries({ queryKey: keyRef.current })
    const unsubscribes = list.map((topic) =>
      manager.subscribe(
        topic,
        (evt) => {
          const verdict = onEventRef.current?.(evt)
          if (verdict !== false) invalidate()
        },
        invalidate, // onReconnect
      ),
    )
    return () => unsubscribes.forEach((off) => off())
  }, [manager, queryClient, topicsKey, liveEnabled])

  return result
}
